/**
 * Calls `onResume` when the app is probably usable again after a stretch it
 * could not observe: the tab came back into view, the window regained focus,
 * or the network reconnected. Waking a laptop from sleep or hibernate produces
 * at least one of these, while timers set before the sleep may fire late or
 * all at once.
 *
 * Several of these usually fire together, so handlers must be cheap and safe
 * to call repeatedly.
 *
 * @returns a function that removes the listeners.
 */
export const subscribeToAppResume = (onResume: () => void): (() => void) => {
  const handleResume = (): void => {
    onResume();
  };

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === "visible") {
      onResume();
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("focus", handleResume);
  window.addEventListener("online", handleResume);

  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("focus", handleResume);
    window.removeEventListener("online", handleResume);
  };
};
